import { type Skeleton, type SkinnedMesh } from 'three'
import { PoseToRig } from './PoseToRig'
import { type PoseProvider } from './PoseProvider'
import { MediaPipePoseProvider } from './MediaPipePoseProvider'
import { MediaPipeHandProvider } from './MediaPipeHandProvider'
import { HandToRig } from './HandToRig'

/**
 * Owns the webcam + render-loop lifecycle for live mocap mode. Provider-agnostic:
 * the actual pose-detection work is delegated to a `PoseProvider` instance
 * (MediaPipe by default, or TF.js BlazePose).
 *
 * Flow on `start()`:
 *   1. Request webcam permission via `navigator.mediaDevices.getUserMedia`.
 *   2. Create a `<video>` element backed by the webcam stream.
 *   3. `provider.init()` — lazy-loads model + runtime (CDN download on first run).
 *   4. Bind `PoseToRig` to the supplied skeleton.
 *   5. Start a `requestAnimationFrame` loop that:
 *        a. Calls `provider.detect(video)`,
 *        b. Passes the 33-element world-landmark result to `PoseToRig.update()`.
 *
 * `stop()` reverses everything: cancels RAF, stops media tracks, disposes the
 * provider, removes the video element. Idempotent.
 *
 * State machine: idle → starting → running → idle (or → error).
 */

export type MocapState = 'idle' | 'starting' | 'running' | 'error'

export interface MocapStartOptions {
  /** SkinnedMesh whose skeleton will be driven. Required. */
  skinned_mesh: SkinnedMesh
  /**
   * Optional container element. When provided, the webcam `<video>` element is
   * appended visibly into this container (with a `mocap-preview-video-element`
   * class for CSS to size/mirror). When omitted, the video is hidden and lives
   * on `document.body` only for MediaPipe to read from.
   */
  preview_container?: HTMLElement
  /** Optional callback that fires whenever state changes (for UI updates). */
  on_state_change?: (state: MocapState, error?: Error) => void
  /**
   * Optional per-frame hook fired after each successful pose-to-rig update with
   * the timestamp (in seconds, monotonic). Used by the recording feature to bake
   * keyframes into a user clip — the consumer reads the bone state from its own
   * skinned mesh ref and writes via `KeyframeBuilder`.
   */
  on_pose_update?: (frame_time_seconds: number) => void
  /**
   * Bone names to NOT drive from mocap. Applied to PoseToRig after bind(). The
   * bones revert to their bind-pose rotation immediately. Persisted across
   * sessions by the consumer (typically via `MocapBoneSettings`).
   */
  disabled_bones?: string[]
  /**
   * Bone whose XZ position should be driven from the hip-midpoint landmark when
   * root motion is enabled. Typically `pelvis` for humanoid rigs (from
   * `RigConfig.position_tracking_bone_name`).
   */
  root_motion_bone_name?: string
  /** Initial root-motion enable state. The user can toggle this at runtime via `set_root_motion_enabled`. */
  root_motion_enabled?: boolean
  /**
   * Initial confidence-weighted smoothing strength (0..4). 0 = no extra
   * smoothing beyond the OneEuroFilter defaults. Higher values dampen frames
   * with low landmark visibility more aggressively. Tunable live via
   * `set_smoothing_strength`.
   */
  smoothing_strength?: number
  /**
   * Pose-detection backend. Defaults to MediaPipe Tasks Vision when omitted,
   * preserving back-compat with callers that don't yet pass a provider.
   */
  provider?: PoseProvider
  /**
   * Enable hand-mocap layer alongside the body. When true, a `HandLandmarker`
   * provider is loaded (~5MB extra model from CDN) and finger bones get
   * driven each frame in addition to the body bones. Off by default since
   * many rigs don't have finger bones at all (e.g. Tripo without fingers,
   * Single-Hand-Bone variants).
   */
  hands_enabled?: boolean
}

export class MocapController {
  private state: MocapState = 'idle'
  private on_state_change: ((state: MocapState, error?: Error) => void) | null = null
  private on_pose_update: ((frame_time_seconds: number) => void) | null = null

  private video: HTMLVideoElement | null = null
  private stream: MediaStream | null = null
  private provider: PoseProvider | null = null
  private pose_to_rig: PoseToRig | null = null
  private skeleton: Skeleton | null = null
  // Hand-layer state. Both null when hands aren't enabled; both set together
  // by `enable_hands_runtime`/`disable_hands_runtime`. Held alongside the
  // body provider so a single RAF tick can update both per frame.
  private hand_provider: MediaPipeHandProvider | null = null
  private hand_to_rig: HandToRig | null = null
  private hand_init_in_flight: boolean = false

  private raf_handle: number | null = null

  get_state (): MocapState {
    return this.state
  }

  is_active (): boolean {
    return this.state === 'starting' || this.state === 'running'
  }

  async start (options: MocapStartOptions): Promise<void> {
    if (this.is_active()) return // idempotent
    this.on_state_change = options.on_state_change ?? null
    this.on_pose_update = options.on_pose_update ?? null
    this.set_state('starting')

    try {
      // 1. Webcam stream
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480, facingMode: 'user' },
          audio: false
        })
      } catch (err) {
        // Translate the browser's terse DOMException names into something a
        // hobbyist can act on. The Error we throw here gets surfaced via the
        // on_state_change(error) callback as a modal title/body.
        throw friendlier_webcam_error(err)
      }

      // 2. Video element backed by the stream. Mounted into the caller's preview
      //    container if provided (visible, styled by CSS); otherwise hidden on
      //    document.body so MediaPipe can still read frames from it.
      this.video = document.createElement('video')
      this.video.autoplay = true
      this.video.muted = true
      this.video.playsInline = true
      this.video.srcObject = this.stream
      if (options.preview_container !== undefined) {
        this.video.classList.add('mocap-preview-video-element')
        options.preview_container.appendChild(this.video)
      } else {
        this.video.style.display = 'none'
        document.body.appendChild(this.video)
      }
      await new Promise<void>((resolve, reject) => {
        if (this.video === null) { reject(new Error('Video element vanished')); return }
        this.video.onloadeddata = () => { resolve() }
        this.video.onerror = () => { reject(new Error('Failed to load webcam video')) }
      })

      // 3. Pose-detection provider — lazy-loads its runtime + model from CDN on
      //    first activation (see MediaPipePoseProvider / TfjsBlazePoseProvider).
      this.provider = options.provider ?? new MediaPipePoseProvider()
      await this.provider.init()

      // 4. Bind PoseToRig
      this.skeleton = options.skinned_mesh.skeleton
      this.pose_to_rig = new PoseToRig({
        root_motion_bone_name: options.root_motion_bone_name,
        smoothing_strength: options.smoothing_strength
      })
      this.pose_to_rig.bind(this.skeleton)
      if (!this.pose_to_rig.is_bound()) {
        throw new Error('No driveable bones found on this skeleton — mocap requires a humanoid rig.')
      }
      if (options.disabled_bones !== undefined) {
        this.pose_to_rig.set_disabled_bones(options.disabled_bones)
      }
      if (options.root_motion_enabled === true) {
        this.pose_to_rig.set_root_motion_enabled(true)
      }

      // 5. Hand layer (optional). We init the hand provider AFTER the body
      //    pipeline is good to go so a hand-init failure doesn't tear down
      //    body mocap — hand mocap is a supplemental layer, not a hard
      //    requirement. The runtime toggle (`enable_hands_runtime`) reuses
      //    the same code path so the user can flip hands on/off mid-session.
      if (options.hands_enabled === true) {
        await this.enable_hands_runtime()
      }

      // 6. Render loop
      this.set_state('running')
      this.schedule_frame()
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      console.error('Mocap start failed:', error)
      this.set_state('error', error)
      await this.cleanup()
    }
  }

  async stop (): Promise<void> {
    if (this.state === 'idle') return
    await this.cleanup()
    this.set_state('idle')
  }

  /**
   * Toggle a bone's mocap-driving on the live PoseToRig. No-op when mocap isn't
   * running — caller is responsible for persisting the choice and re-applying
   * on next start (via the `disabled_bones` start option).
   */
  set_bone_enabled (bone_name: string, enabled: boolean): void {
    this.pose_to_rig?.set_bone_enabled(bone_name, enabled)
  }

  /**
   * Toggle root-motion driving on the live PoseToRig. No-op when mocap isn't
   * running. Caller is responsible for persisting the choice and re-applying on
   * next start (via the `root_motion_enabled` start option).
   */
  set_root_motion_enabled (enabled: boolean): void {
    this.pose_to_rig?.set_root_motion_enabled(enabled)
  }

  /**
   * Update the confidence-weighted smoothing strength on the live PoseToRig.
   * No-op when mocap isn't running. Caller persists and re-passes via
   * `smoothing_strength` on next start.
   */
  set_smoothing_strength (strength: number): void {
    this.pose_to_rig?.set_smoothing_strength(strength)
  }

  private schedule_frame (): void {
    this.raf_handle = requestAnimationFrame(() => { void this.run_frame() })
  }

  private async run_frame (): Promise<void> {
    if (this.state !== 'running' || this.video === null || this.provider === null || this.pose_to_rig === null) return

    // The provider is async — TF.js inference takes ~30-50ms per frame, MediaPipe
    // is effectively instant. We await detect() so frames don't pile up faster
    // than inference can run, then schedule the next RAF AFTER the await. This
    // naturally caps frame rate at inference speed.
    let world_landmarks: ReturnType<typeof this.provider.detect> extends Promise<infer R> ? R : never
    try {
      world_landmarks = await this.provider.detect(this.video)
    } catch (err) {
      console.warn('Mocap frame failed (will keep trying):', err)
      world_landmarks = null
    }

    // Guard against state having changed during the await (stop() while inferring).
    if (this.state !== 'running' || this.pose_to_rig === null) return

    if (world_landmarks !== null && world_landmarks.length > 0) {
      const frame_time = performance.now() / 1000
      this.pose_to_rig.update(world_landmarks, frame_time)
      // Hand layer runs after body so the finger bones see an updated
      // hand parent (lower-arm) world transform. We don't gate on
      // body-detect succeeding — hands can still update on frames where
      // the body fell out of confidence, since they're independent
      // models. Per-frame failures are non-fatal (warned).
      if (this.hand_provider !== null && this.hand_to_rig !== null) {
        try {
          const detected_hands = await this.hand_provider.detect(this.video)
          if (this.state === 'running' && detected_hands !== null && detected_hands.length > 0) {
            this.hand_to_rig.update(detected_hands, frame_time)
          }
        } catch (err) {
          console.warn('Mocap hand frame failed (will keep trying):', err)
        }
      }
      this.on_pose_update?.(frame_time)
    }

    this.schedule_frame()
  }

  private async cleanup (): Promise<void> {
    if (this.raf_handle !== null) {
      cancelAnimationFrame(this.raf_handle)
      this.raf_handle = null
    }

    if (this.stream !== null) {
      for (const track of this.stream.getTracks()) track.stop()
      this.stream = null
    }

    if (this.video !== null) {
      this.video.srcObject = null
      if (this.video.parentNode !== null) this.video.parentNode.removeChild(this.video)
      this.video = null
    }

    if (this.provider !== null) {
      try { await this.provider.dispose() } catch (_e) { /* swallow */ }
      this.provider = null
    }

    if (this.hand_provider !== null) {
      try { await this.hand_provider.dispose() } catch (_e) { /* swallow */ }
      this.hand_provider = null
    }

    this.pose_to_rig = null
    this.hand_to_rig = null
    this.skeleton = null
    this.on_pose_update = null
  }

  /**
   * Bring up the hand-mocap layer on a session that's already running. Loads
   * the HandLandmarker model from CDN (~5MB on first activation, browser-
   * cached after) and binds a fresh `HandToRig` to the current skeleton. No-op
   * if hands are already enabled or the session isn't bound to a skeleton.
   */
  async enable_hands_runtime (): Promise<void> {
    if (this.hand_provider !== null) return // already on
    if (this.skeleton === null) return // mocap hasn't bound yet
    if (this.hand_init_in_flight) return // double-click guard
    this.hand_init_in_flight = true
    try {
      const provider = new MediaPipeHandProvider()
      await provider.init()
      // Guard against the user disabling mid-init.
      if (this.skeleton === null) {
        await provider.dispose()
        return
      }
      const hand_to_rig = new HandToRig()
      hand_to_rig.bind(this.skeleton)
      this.hand_provider = provider
      this.hand_to_rig = hand_to_rig
    } finally {
      this.hand_init_in_flight = false
    }
  }

  /**
   * Tear down the hand layer. Snaps every driven finger bone back to its
   * bind pose so they don't freeze at the last detected curl, then disposes
   * the provider. Safe to call when hands aren't enabled.
   */
  async disable_hands_runtime (): Promise<void> {
    if (this.hand_to_rig !== null) {
      this.hand_to_rig.reset_to_bind_pose()
      this.hand_to_rig = null
    }
    if (this.hand_provider !== null) {
      const p = this.hand_provider
      this.hand_provider = null
      try { await p.dispose() } catch (_e) { /* swallow */ }
    }
  }

  is_hands_enabled (): boolean {
    return this.hand_provider !== null
  }

  private set_state (state: MocapState, error?: Error): void {
    this.state = state
    this.on_state_change?.(state, error)
  }
}

/**
 * Map a browser MediaDevices error to a user-actionable message. The browser's
 * native messages are technically accurate but unhelpful ("Permission denied"
 * doesn't tell a user what to do).
 */
function friendlier_webcam_error (err: unknown): Error {
  const e = err as { name?: string, message?: string }
  switch (e?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return new Error('Webcam permission was denied. Click the camera icon in your browser\'s address bar to grant access, then try again.')
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return new Error('No webcam was detected. Make sure a camera is connected and visible to your operating system.')
    case 'NotReadableError':
    case 'TrackStartError':
      return new Error('Your webcam is in use by another application. Close any video-call apps (Zoom, Meet, OBS, etc.) and try again.')
    case 'OverconstrainedError':
      return new Error('Your webcam doesn\'t support the requested resolution (640×480). Try a different camera.')
    case 'SecurityError':
      return new Error('Webcam access is blocked by browser security policy. Mocap requires HTTPS or localhost.')
    default: {
      const msg = e?.message ?? String(err)
      return new Error(`Couldn't start the webcam: ${msg}`)
    }
  }
}
